import { FunctionComponent, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import PersonIcon from '@mui/icons-material/Person';
import MedicationIcon from '@mui/icons-material/Medication';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TimelineIcon from '@mui/icons-material/Timeline';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import StarIcon from '@mui/icons-material/Star';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import InfoIcon from '@mui/icons-material/Info';
import styles from './AISearchResultList.module.css';

interface TimelineItem {
  week: number;
  title: string;
  description: string;
  type: 'appointment' | 'medication' | 'test' | 'milestone';
  provider?: string;
}

interface Provider {
  npi: string;
  name: string;
  specialty: string;
  rating: number;
  distance: string;
  nextAvailable: string;
  estimatedCost: number;
}

interface Medication {
  ndc: string;
  name: string;
  dosage: string;
  frequency: string;
  duration: string;
  estimatedCost: number;
  genericAvailable: boolean;
}

interface Procedure {
  cpt: string;
  name: string;
  category: string;
  frequency: string;
  estimatedCost: number;
  coverage: string;
}

interface CarePlanDetails {
  id: string;
  title: string;
  description: string;
  condition: string;
  duration: string;
  estimatedCost: {
    min: number;
    max: number;
  };
  overview: string;
  goals: string[];
  providers: Provider[];
  medications: Medication[];
  procedures: Procedure[];
  timeline: TimelineItem[];
  expectedOutcomes: {
    title: string;
    probability: number;
  }[];
  prerequisites: string[];
  contraindications: string[];
}

const AISearchResultList: FunctionComponent = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [carePlan, setCarePlan] = useState<CarePlanDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'providers' | 'medications' | 'procedures' | 'timeline'>('overview');

  useEffect(() => {
    const fetchCarePlanDetails = async () => {
      setIsLoading(true);
      
      // Mock data for care plan details
      const mockCarePlan: CarePlanDetails = {
        id: '1',
        title: 'Comprehensive Diabetes Management Plan',
        description: 'A personalized approach to managing Type 2 diabetes through lifestyle changes, medication, and regular monitoring.',
        condition: 'Type 2 Diabetes',
        duration: '6-12 months',
        estimatedCost: {
          min: 2500,
          max: 5000
        },
        overview: 'This comprehensive plan combines medical management, lifestyle modifications, and regular monitoring to help you effectively manage Type 2 diabetes. The program is designed to improve blood sugar control, reduce complications, and enhance your overall quality of life.',
        goals: [
          'Achieve HbA1c levels below 7%',
          'Maintain healthy weight through diet and exercise',
          'Prevent diabetes-related complications',
          'Improve overall cardiovascular health',
          'Develop sustainable healthy habits'
        ],
        providers: [
          {
            npi: '1234567890',
            name: 'Dr. Sarah Johnson',
            specialty: 'Endocrinology',
            rating: 4.8,
            distance: '2.5 mi',
            nextAvailable: '2024-01-15',
            estimatedCost: 150
          },
          {
            npi: '0987654321',
            name: 'Maria Rodriguez, RD',
            specialty: 'Registered Dietitian',
            rating: 4.9,
            distance: '3.1 mi',
            nextAvailable: '2024-01-12',
            estimatedCost: 100
          },
          {
            npi: '1122334455',
            name: 'Dr. Michael Chen',
            specialty: 'Primary Care',
            rating: 4.7,
            distance: '1.8 mi',
            nextAvailable: '2024-01-10',
            estimatedCost: 120
          }
        ],
        medications: [
          {
            ndc: '12345-678-90',
            name: 'Metformin',
            dosage: '500mg',
            frequency: 'Twice daily',
            duration: 'Ongoing',
            estimatedCost: 30,
            genericAvailable: true
          },
          {
            ndc: '09876-543-21',
            name: 'Glipizide',
            dosage: '5mg',
            frequency: 'Once daily',
            duration: 'As needed',
            estimatedCost: 25,
            genericAvailable: true
          }
        ],
        procedures: [
          {
            cpt: '83036',
            name: 'Hemoglobin A1c Test',
            category: 'Laboratory',
            frequency: 'Every 3 months',
            estimatedCost: 40,
            coverage: '100% covered'
          },
          {
            cpt: '82947',
            name: 'Glucose Test',
            category: 'Laboratory',
            frequency: 'Monthly',
            estimatedCost: 25,
            coverage: '100% covered'
          },
          {
            cpt: '99213',
            name: 'Office Visit - Follow-up',
            category: 'Evaluation & Management',
            frequency: 'Monthly',
            estimatedCost: 150,
            coverage: '80% covered'
          }
        ],
        timeline: [
          {
            week: 1,
            title: 'Initial Consultation',
            description: 'Meet with endocrinologist for comprehensive evaluation',
            type: 'appointment',
            provider: 'Dr. Sarah Johnson'
          },
          {
            week: 1,
            title: 'Baseline Lab Work',
            description: 'Complete initial blood tests including HbA1c, lipid panel',
            type: 'test'
          },
          {
            week: 2,
            title: 'Nutrition Counseling',
            description: 'First session with registered dietitian',
            type: 'appointment',
            provider: 'Maria Rodriguez, RD'
          },
          {
            week: 2,
            title: 'Start Medication',
            description: 'Begin Metformin as prescribed',
            type: 'medication'
          },
          {
            week: 4,
            title: 'First Follow-up',
            description: 'Check medication tolerance and initial progress',
            type: 'appointment',
            provider: 'Dr. Michael Chen'
          },
          {
            week: 12,
            title: 'Progress Evaluation',
            description: 'Review 3-month HbA1c results and adjust plan',
            type: 'milestone'
          }
        ],
        expectedOutcomes: [
          {
            title: 'HbA1c reduction of 1-2%',
            probability: 85
          },
          {
            title: 'Weight loss of 5-10%',
            probability: 70
          },
          {
            title: 'Improved blood pressure control',
            probability: 75
          },
          {
            title: 'Reduced medication needs',
            probability: 60
          }
        ],
        prerequisites: [
          'Recent diagnosis of Type 2 Diabetes or prediabetes',
          'Commitment to lifestyle changes',
          'Ability to attend regular appointments',
          'No severe kidney or liver disease'
        ],
        contraindications: [
          'Type 1 Diabetes',
          'Pregnancy or planning pregnancy',
          'Severe kidney disease (eGFR < 30)',
          'History of lactic acidosis'
        ]
      };
      
      // Simulate API delay
      setTimeout(() => {
        setCarePlan(mockCarePlan);
        setIsLoading(false);
      }, 600);
    };

    if (id) {
      fetchCarePlanDetails();
    }
  }, [id]);

  const renderOverview = () => (
    <div className={styles.overviewContent}>
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Overview</h3>
        <p className={styles.overviewText}>{carePlan?.overview}</p>
      </div>

      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Treatment Goals</h3>
        <ul className={styles.goalsList}>
          {carePlan?.goals.map((goal, index) => (
            <li key={index}>
              <CheckCircleIcon className={styles.goalIcon} />
              <span>{goal}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Expected Outcomes</h3>
        <div className={styles.outcomesGrid}>
          {carePlan?.expectedOutcomes.map((outcome, index) => (
            <div key={index} className={styles.outcomeCard}>
              <div className={styles.outcomeHeader}>
                <TrendingUpIcon className={styles.outcomeIcon} />
                <span className={styles.outcomeProbability}>{outcome.probability}%</span>
              </div>
              <p className={styles.outcomeTitle}>{outcome.title}</p>
            </div>
          ))}
        </div>
      </div>

      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Prerequisites</h3>
        <ul className={styles.prereqList}>
          {carePlan?.prerequisites.map((prereq, index) => (
            <li key={index}>{prereq}</li>
          ))}
        </ul>
      </div>

      <div className={styles.warningSection}>
        <InfoIcon className={styles.warningIcon} />
        <div>
          <h4 className={styles.warningTitle}>Contraindications</h4>
          <ul className={styles.contraindicationsList}>
            {carePlan?.contraindications.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );

  const renderProviders = () => (
    <div className={styles.providersContent}>
      <h3 className={styles.contentTitle}>Care Team</h3>
      <div className={styles.providersGrid}>
        {carePlan?.providers.map((provider) => (
          <div 
            key={provider.npi} 
            className={styles.providerCard}
            onClick={() => navigate(`/provider/${provider.npi}`)}
          >
            <div className={styles.providerHeader}>
              <div className={styles.providerIcon}>
                <PersonIcon />
              </div>
              <div className={styles.providerInfo}>
                <h4 className={styles.providerName}>{provider.name}</h4>
                <p className={styles.providerSpecialty}>{provider.specialty}</p>
              </div>
            </div>
            <div className={styles.providerStats}>
              <div className={styles.statItem}>
                <StarIcon className={styles.statIcon} />
                <span>{provider.rating}</span>
              </div>
              <div className={styles.statItem}>
                <LocationOnIcon className={styles.statIcon} />
                <span>{provider.distance}</span>
              </div>
              <div className={styles.statItem}>
                <CalendarMonthIcon className={styles.statIcon} />
                <span>Next: {new Date(provider.nextAvailable).toLocaleDateString()}</span>
              </div>
            </div>
            <div className={styles.providerCost}>
              <AttachMoneyIcon className={styles.costIcon} />
              <span>${provider.estimatedCost} per visit</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderMedications = () => (
    <div className={styles.medicationsContent}>
      <h3 className={styles.contentTitle}>Medications</h3>
      <div className={styles.medicationsGrid}>
        {carePlan?.medications.map((medication) => (
          <div 
            key={medication.ndc} 
            className={styles.medicationCard}
            onClick={() => navigate(`/medication/${medication.ndc}`)}
          >
            <div className={styles.medicationHeader}>
              <MedicationIcon className={styles.medicationIcon} />
              <div className={styles.medicationInfo}>
                <h4 className={styles.medicationName}>{medication.name}</h4>
                <p className={styles.medicationDosage}>{medication.dosage}</p>
              </div>
              {medication.genericAvailable && (
                <span className={styles.genericBadge}>Generic</span>
              )}
            </div>
            <div className={styles.medicationDetails}>
              <p><strong>Frequency:</strong> {medication.frequency}</p>
              <p><strong>Duration:</strong> {medication.duration}</p>
              <p className={styles.medicationCost}>
                <AttachMoneyIcon className={styles.costIcon} />
                <span>${medication.estimatedCost}/month</span>
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderProcedures = () => (
    <div className={styles.proceduresContent}>
      <h3 className={styles.contentTitle}>Procedures & Tests</h3>
      <div className={styles.proceduresGrid}>
        {carePlan?.procedures.map((procedure) => (
          <div 
            key={procedure.cpt} 
            className={styles.procedureCard}
            onClick={() => navigate(`/procedure/${procedure.cpt}`)}
          >
            <div className={styles.procedureHeader}>
              <MedicalServicesIcon className={styles.procedureIcon} />
              <div className={styles.procedureInfo}>
                <h4 className={styles.procedureName}>{procedure.name}</h4>
                <p className={styles.procedureCategory}>{procedure.category}</p>
              </div>
            </div>
            <div className={styles.procedureDetails}>
              <p><strong>Frequency:</strong> {procedure.frequency}</p>
              <div className={styles.procedureCost}>
                <AttachMoneyIcon className={styles.costIcon} />
                <span>${procedure.estimatedCost}</span>
                <span className={styles.coverage}>({procedure.coverage})</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderTimeline = () => (
    <div className={styles.timelineContent}>
      <h3 className={styles.contentTitle}>Treatment Timeline</h3>
      <div className={styles.timeline}>
        {carePlan?.timeline.map((item, index) => (
          <div key={index} className={styles.timelineItem}>
            <div className={styles.timelineMarker}>
              <div className={`${styles.markerDot} ${styles[item.type]}`}></div>
              {index < carePlan.timeline.length - 1 && <div className={styles.timelineLine}></div>}
            </div>
            <div className={styles.timelineContent}>
              <div className={styles.timelineHeader}>
                <h4 className={styles.timelineTitle}>{item.title}</h4>
                <span className={styles.timelineWeek}>Week {item.week}</span>
              </div>
              <p className={styles.timelineDescription}>{item.description}</p>
              {item.provider && (
                <p className={styles.timelineProvider}>
                  <PersonIcon className={styles.providerIcon} />
                  {item.provider}
                </p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className={styles.loading}>
        <div className={styles.spinner}></div>
        <p>Loading care plan details...</p>
      </div>
    );
  }

  if (!carePlan) {
    return null;
  }

  return (
    <div className={styles.aiSearchResultList}>
      <div className={styles.header}>
        <button className={styles.backButton} onClick={() => navigate(-1)}>
          <ArrowBackIcon />
        </button>
        <div className={styles.headerContent}>
          <h1 className={styles.headerTitle}>Care Plan Details</h1>
          <span className={styles.aiLabel}>
            <AutoAwesomeIcon className={styles.aiIcon} />
            AI Generated
          </span>
        </div>
      </div>

      <div className={styles.planHeader}>
        <div className={styles.planIcon}>
          <HealthAndSafetyIcon />
        </div>
        <div className={styles.planInfo}>
          <h2 className={styles.planTitle}>{carePlan.title}</h2>
          <p className={styles.planCondition}>{carePlan.condition}</p>
          <div className={styles.planMeta}>
            <span className={styles.metaItem}>
              <CalendarMonthIcon className={styles.metaIcon} />
              {carePlan.duration}
            </span>
            <span className={styles.metaItem}>
              <AttachMoneyIcon className={styles.metaIcon} />
              ${carePlan.estimatedCost.min.toLocaleString()}-${carePlan.estimatedCost.max.toLocaleString()}
            </span>
          </div>
        </div>
      </div>

      <div className={styles.tabsContainer}>
        <div className={styles.tabs}>
          <button 
            className={`${styles.tab} ${activeTab === 'overview' ? styles.active : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            Overview
          </button>
          <button 
            className={`${styles.tab} ${activeTab === 'providers' ? styles.active : ''}`}
            onClick={() => setActiveTab('providers')}
          >
            Providers ({carePlan.providers.length})
          </button>
          <button 
            className={`${styles.tab} ${activeTab === 'medications' ? styles.active : ''}`}
            onClick={() => setActiveTab('medications')}
          >
            Medications ({carePlan.medications.length})
          </button>
          <button 
            className={`${styles.tab} ${activeTab === 'procedures' ? styles.active : ''}`}
            onClick={() => setActiveTab('procedures')}
          >
            Procedures ({carePlan.procedures.length})
          </button>
          <button 
            className={`${styles.tab} ${activeTab === 'timeline' ? styles.active : ''}`}
            onClick={() => setActiveTab('timeline')}
          >
            Timeline
          </button>
        </div>
      </div>

      <div className={styles.content}>
        {activeTab === 'overview' && renderOverview()}
        {activeTab === 'providers' && renderProviders()}
        {activeTab === 'medications' && renderMedications()}
        {activeTab === 'procedures' && renderProcedures()}
        {activeTab === 'timeline' && renderTimeline()}
      </div>

      <div className={styles.actions}>
        <button className={styles.startPlanButton}>
          Start This Care Plan
          <ChevronRightIcon className={styles.chevronIcon} />
        </button>
        <button className={styles.saveButton}>
          Save for Later
        </button>
      </div>
    </div>
  );
};

export default AISearchResultList;